import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, ChevronRight, Sparkles, Zap, Brain, Video, Headphones, Activity, Lock, 
  BookOpen, Mic, Lightbulb, AlertTriangle, TrendingUp, Shield, Target, Users, 
  MessageCircle, Calendar, BarChart3, FileText, Heart
} from 'lucide-react';
import { useFeatureGate } from '../contexts/FeatureGateContext';
import { useDemoMode } from '../contexts/DemoModeContext';
import confetti from 'canvas-confetti';

const FeatureUnlockNotification: React.FC = () => {
  const { newlyUnlockedFeatures, dismissNewFeature } = useFeatureGate();
  const { isDemoMode } = useDemoMode();
  const [currentFeature, setCurrentFeature] = useState<string | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [isMultipleFeatures, setIsMultipleFeatures] = useState(false);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const initialDelayRef = useRef<NodeJS.Timeout | null>(null);
  const confettiCanvasRef = useRef<HTMLCanvasElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Don't show notifications in demo mode
    if (isDemoMode) {
      return;
    }

    if (newlyUnlockedFeatures.length > 0 && !currentFeature) {
      setIsMultipleFeatures(newlyUnlockedFeatures.length > 1);

      // Set a delay before showing the first notification
      // Use a longer delay to ensure user is engaged
      initialDelayRef.current = setTimeout(() => {
        setCurrentFeature(newlyUnlockedFeatures[0]);
        setShowNotification(true);

        // Trigger confetti for celebration
        if (typeof window !== 'undefined') {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        }
      }, 1000); // 1 second delay after trigger
    }

    return () => {
      if (initialDelayRef.current) {
        clearTimeout(initialDelayRef.current);
      }
    };
  }, [newlyUnlockedFeatures, currentFeature, isDemoMode]);

  const handleDismiss = () => {
    if (!currentFeature) return;
    
    dismissNewFeature(currentFeature);
    setShowNotification(false);
    
    // Check if there are more features to show
    if (currentFeatureIndex < newlyUnlockedFeatures.length - 1) {
      // Move to the next feature
      setCurrentFeatureIndex(prev => prev + 1);
      
      // Show the next feature after a short delay
      setTimeout(() => {
        setCurrentFeature(newlyUnlockedFeatures[currentFeatureIndex + 1]);
        setShowNotification(true);
      }, 500);
    } else {
      // No more features to show
      setCurrentFeature(null);
      setCurrentFeatureIndex(0);
    }
  };

  const handleNext = () => {
    if (!currentFeature) return;
    
    dismissNewFeature(currentFeature);
    setShowNotification(false);
    
    // Check if there are more features to show
    if (currentFeatureIndex < newlyUnlockedFeatures.length - 1) {
      // Move to the next feature
      setCurrentFeatureIndex(prev => prev + 1);
      
      // Show the next feature after a short delay
      setTimeout(() => {
        setCurrentFeature(newlyUnlockedFeatures[currentFeatureIndex + 1]);
        setShowNotification(true);
        
        // Trigger a smaller confetti burst for subsequent features
        if (typeof window !== 'undefined') {
          confetti({
            particleCount: 50,
            spread: 50,
            origin: { y: 0.7 }
          });
        }
      }, 500);
    } else {
      // No more features to show
      setCurrentFeature(null);
      setCurrentFeatureIndex(0);
    }
  };

  const handleExplore = () => {
    if (!currentFeature) return;
    
    dismissNewFeature(currentFeature);
    setShowNotification(false);
    setCurrentFeature(null);
    setCurrentFeatureIndex(0);
    navigate(getFeatureDetails(currentFeature).path);
  };

  const getFeatureDetails = (featureId: string) => {
    const featureMap: Record<string, { 
      title: string; 
      description: string; 
      icon: React.ElementType; 
      path: string;
      gradientColor: string;
      iconBgColor: string;
      iconTextColor: string;
      shape: 'rounded-xl' | 'rounded-full' | 'rounded-lg' | 'rounded-2xl';
      pattern: 'dots' | 'lines' | 'waves' | 'grid' | 'none';
      animation: string;
    }> = {
      'journal_entry': {
        title: 'Journal Entries',
        description: 'Express your thoughts and track your mood with text journaling',
        icon: BookOpen,
        path: '/journal/new',
        gradientColor: 'from-amber-400 to-amber-500',
        iconBgColor: 'bg-amber-100',
        iconTextColor: 'text-amber-600',
        shape: 'rounded-xl',
        pattern: 'lines',
        animation: 'animate-none'
      },
      'voice_journal': {
        title: 'Voice Journaling',
        description: 'Record your thoughts naturally with voice-to-text transcription',
        icon: Mic,
        path: '/journal/voice',
        gradientColor: 'from-rose-400 to-rose-500',
        iconBgColor: 'bg-rose-100',
        iconTextColor: 'text-rose-600',
        shape: 'rounded-lg',
        pattern: 'waves',
        animation: 'animate-none'
      },
      'basic_insights': {
        title: 'AI Insights',
        description: 'Get personalized insights about your mental health patterns',
        icon: Lightbulb,
        path: '/analysis',
        gradientColor: 'from-yellow-400 to-yellow-500',
        iconBgColor: 'bg-yellow-100',
        iconTextColor: 'text-yellow-600',
        shape: 'rounded-2xl',
        pattern: 'dots',
        animation: 'animate-none'
      },
      'crisis_support': {
        title: 'Crisis Support',
        description: 'Access immediate help and resources during difficult times',
        icon: AlertTriangle,
        path: '/crisis',
        gradientColor: 'from-red-600 to-red-700',
        iconBgColor: 'bg-red-100',
        iconTextColor: 'text-red-600',
        shape: 'rounded-xl',
        pattern: 'none',
        animation: 'animate-none'
      },
      'simple_progress': {
        title: 'Progress Tracking',
        description: 'Track your mental wellness journey with visual insights',
        icon: TrendingUp,
        path: '/dashboard',
        gradientColor: 'from-lime-400 to-lime-500',
        iconBgColor: 'bg-lime-100',
        iconTextColor: 'text-lime-600',
        shape: 'rounded-lg',
        pattern: 'grid',
        animation: 'animate-none'
      },
      'avatar_companion': {
        title: 'AI Avatar Companion',
        description: 'Have face-to-face conversations with your personalized AI therapist',
        icon: Video,
        path: '/avatar',
        gradientColor: 'from-purple-500 to-purple-600',
        iconBgColor: 'bg-purple-100',
        iconTextColor: 'text-purple-600',
        shape: 'rounded-xl',
        pattern: 'dots',
        animation: 'animate-pulse'
      },
      'audio_library': {
        title: 'Audio Library',
        description: 'Access personalized meditations, affirmations, and sleep stories',
        icon: Headphones,
        path: '/audio',
        gradientColor: 'from-green-500 to-green-600',
        iconBgColor: 'bg-green-100',
        iconTextColor: 'text-green-600',
        shape: 'rounded-full',
        pattern: 'waves',
        animation: 'animate-bounce'
      },
      'advanced_analytics': {
        title: 'Advanced Analytics',
        description: 'Gain deeper insights into your mental health patterns and progress',
        icon: Brain,
        path: '/analysis',
        gradientColor: 'from-blue-500 to-blue-600',
        iconBgColor: 'bg-blue-100',
        iconTextColor: 'text-blue-600',
        shape: 'rounded-2xl',
        pattern: 'grid',
        animation: 'animate-none'
      },
      'detailed_behavioral': {
        title: 'Behavioral Insights',
        description: 'Understand your behavioral patterns and how they affect your wellbeing',
        icon: Activity,
        path: '/behavioral',
        gradientColor: 'from-purple-400 to-purple-600',
        iconBgColor: 'bg-orange-100',
        iconTextColor: 'text-orange-600',
        shape: 'rounded-lg',
        pattern: 'lines',
        animation: 'animate-none'
      },
      'custom_audio': {
        title: 'Custom Audio Content',
        description: 'Create personalized audio content tailored to your needs',
        icon: Headphones,
        path: '/audio',
        gradientColor: 'from-green-400 to-green-500',
        iconBgColor: 'bg-emerald-100',
        iconTextColor: 'text-emerald-600',
        shape: 'rounded-xl',
        pattern: 'waves',
        animation: 'animate-none'
      },
      'therapist_matching': {
        title: 'Therapist Matching',
        description: 'Connect with licensed therapists who match your needs',
        icon: Users,
        path: '/crisis?tab=therapists',
        gradientColor: 'from-sky-400 to-sky-500',
        iconBgColor: 'bg-sky-100',
        iconTextColor: 'text-sky-600',
        shape: 'rounded-full',
        pattern: 'dots',
        animation: 'animate-none'
      },
      'behavioral_science': {
        title: 'Detailed Behavioral Insights',
        description: 'Access advanced behavioral science tools and insights',
        icon: Brain,
        path: '/behavioral',
        gradientColor: 'from-orange-400 to-orange-500',
        iconBgColor: 'bg-orange-100', 
        iconTextColor: 'text-orange-600', 
        shape: 'rounded-2xl',
        pattern: 'grid',
        animation: 'animate-none'
      },
      'research_participation': {
        title: 'Personalized Audio Content',
        description: 'Contribute to mental health research and help others',
        icon: Zap,
        path: '/consent',
        gradientColor: 'from-emerald-400 to-emerald-500',
        iconBgColor: 'bg-rose-100',
        iconTextColor: 'text-rose-600',
        shape: 'rounded-lg',
        pattern: 'lines',
        animation: 'animate-none'
      },
      'advanced_privacy': {
        title: 'Professional Therapist Matching',
        description: 'Fine-tune your privacy settings with granular controls',
        icon: Shield,
        path: '/consent',
        gradientColor: 'from-violet-400 to-violet-500',
        iconBgColor: 'bg-violet-100',
        iconTextColor: 'text-violet-600',
        shape: 'rounded-xl',
        pattern: 'none',
        animation: 'animate-none'
      },
      'safety_plan': {
        title: 'Behavioral Science Tools',
        description: 'Create your personalized plan for navigating difficult moments',
        icon: BarChart3,
        path: '/safety-plan',
        gradientColor: 'from-amber-400 to-amber-500',
        iconBgColor: 'bg-blue-100',
        iconTextColor: 'text-blue-600',
        shape: 'rounded-xl',
        pattern: 'none',
        animation: 'animate-none'
      },
      'goal_adjustment': {
        title: 'Goal Setting',
        description: 'Set and track personalized mental health goals',
        icon: FileText,
        path: '/profile',
        gradientColor: 'from-fuchsia-400 to-fuchsia-500',
        iconBgColor: 'bg-indigo-100',
        iconTextColor: 'text-indigo-600',
        shape: 'rounded-lg',
        pattern: 'dots',
        animation: 'animate-none'
      },
      'basic_behavioral': {
        title: 'Behavioral Patterns',
        description: 'Identify patterns in your behavior and emotions',
        icon: Lock,
        path: '/behavioral',
        gradientColor: 'from-slate-400 to-slate-500',
        iconBgColor: 'bg-cyan-100',
        iconTextColor: 'text-cyan-600',
        shape: 'rounded-2xl',
        pattern: 'grid',
        animation: 'animate-none'
      },
      'community_features': {
        title: 'Community Features',
        description: 'Connect with others on similar mental wellness journeys',
        icon: Users,
        path: '/dashboard',
        gradientColor: 'from-pink-400 to-pink-500',
        iconBgColor: 'bg-pink-100',
        iconTextColor: 'text-pink-600',
        shape: 'rounded-lg',
        pattern: 'dots',
        animation: 'animate-none'
      },
    };

    return featureMap[featureId] || {
      title: `New Feature: ${featureId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}`,
      description: 'You\'ve unlocked an exciting new capability in Mira',
      icon: Sparkles,
      path: '/dashboard',
      gradientColor: 'from-teal-500 to-teal-600',
      iconBgColor: 'bg-teal-100',
      iconTextColor: 'text-teal-600',
      shape: 'rounded-xl',
      pattern: 'none',
      animation: 'animate-none'
    };
  };

  const renderPatternBackground = (pattern: string) => {
    switch (pattern) {
      case 'dots':
        return (
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{ 
              backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
              backgroundSize: '15px 15px' 
            }}></div>
          </div>
        );
      case 'lines':
        return (
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{ 
              backgroundImage: 'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '20px 20px' 
            }}></div>
          </div>
        );
      case 'waves':
        return (
          <div className="absolute inset-0 opacity-10">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M0,50 C20,60 40,40 60,50 C80,60 100,40 100,50 L100,100 L0,100 Z" fill="currentColor" />
            </svg>
          </div>
        );
      case 'grid':
        return (
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{ 
              backgroundImage: 'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '10px 10px' 
            }}></div>
          </div>
        );
      default:
        return null;
    }
  };

  if (!currentFeature || !showNotification || isDemoMode) return null;

  const feature = getFeatureDetails(currentFeature);
  const FeatureIcon = feature.icon;
  const isLastFeature = currentFeatureIndex === newlyUnlockedFeatures.length - 1;

  return (
    <div className="fixed bottom-20 right-4 left-4 md:left-auto md:bottom-4 md:right-4 md:max-w-sm w-full z-50">
      <div className={`bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom ${feature.shape}`}>
        <div className={`bg-gradient-to-r ${feature.gradientColor} px-4 py-3 flex justify-between items-center relative`}>
          {renderPatternBackground(feature.pattern)}
          <div className="flex items-center gap-2 text-white relative z-10">
            <Sparkles className="w-5 h-5" />
            <span className="font-semibold">New Feature Unlocked!</span>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/80 hover:text-white relative z-10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4">
          <div className="flex items-center gap-4 mb-3">
            <div className={`w-12 h-12 ${feature.iconBgColor} rounded-full flex items-center justify-center ${feature.animation}`}>
              <FeatureIcon className={`w-6 h-6 ${feature.iconTextColor}`} />
            </div> 
            <div>
              <h3 className="font-bold text-slate-900 text-lg">{feature.title}</h3>
              <p className="text-sm text-slate-600">{feature.description}</p>
            </div>
          </div>
          
          {isMultipleFeatures && (
            <div className="flex justify-center mb-3">
              <div className="flex gap-1">
                {newlyUnlockedFeatures.slice(0, 5).map((featureId, index) => {
                  const featureDetails = getFeatureDetails(featureId);
                  return (
                    <div 
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentFeatureIndex 
                          ? `bg-${featureDetails.iconTextColor.replace('text-', '')}`
                          : 'bg-slate-300'
                      }`}
                    />
                  );
                })}
                {newlyUnlockedFeatures.length > 5 && (
                  <span className="text-xs text-slate-500 ml-1">+{newlyUnlockedFeatures.length - 5}</span>
                )}
              </div>
            </div>
          )}
          
          <button
            onClick={isLastFeature ? handleExplore : handleNext}
            className={`w-full flex items-center justify-center gap-2 bg-gradient-to-r ${feature.gradientColor} hover:opacity-90 text-white py-2 px-4 rounded-lg transition-colors`}
          >
            {isLastFeature ? (
              <>
                Explore Now
                <ChevronRight className="w-4 h-4" />
              </>
            ) : (
              <>
                Next Feature
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeatureUnlockNotification;